/**
 * What a failed money-moving mutation (pay confirm or cancel, exchange accept)
 * tells us about whether the server applied it.
 *
 * - `"definite"`: the server answered and refused. Nothing was applied, so the
 *   handler releases its submit-once guard and the customer can retry.
 * - `"unknown"`: no definite answer came back. The request may have been
 *   applied before the connection dropped, the gateway gave up, or the server
 *   failed after writing. A retry could apply it twice, so the handler re-reads
 *   the resource before deciding (`.claude/rules/web/domains/submit-once.md`
 *   → "Release cases").
 */
export type MutationFailureOutcome = "definite" | "unknown";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isClientErrorStatus(status: number): boolean {
  return status >= 400 && status < 500;
}

/**
 * Classifies a rejected mutation by the RTK Query error shape it carries.
 *
 * Unknown:
 * - `FETCH_ERROR` (no response at all) and `TIMEOUT_ERROR` (the client gave
 *   up waiting);
 * - any numeric 5xx, a plain 500 included;
 * - `PARSING_ERROR` whose `originalStatus` is under 400 (a success the body of
 *   which could not be read) or a 5xx (a gateway HTML error page);
 * - any other numeric status that is not a 4xx, since it is not a refusal.
 *
 * Definite:
 * - any numeric 4xx, including 408 and 429. The transport retry policy
 *   (`redux/services/shouldRetryRequest.ts`) counts those two as transient
 *   for idempotent reads, but for a mutation they are still the server saying
 *   it did not act;
 * - `PARSING_ERROR` whose `originalStatus` is a 4xx;
 * - `CUSTOM_ERROR`, RTK Query's shape for an error raised by client code
 *   rather than reported by the server; it is treated like the status-less
 *   errors below.
 *
 * An error with no `status` at all (a plain `Error`, a `TypeError` from
 * response handling, or a non-object throw) is classified as definite: it
 * carries no sign that a request was in flight when it failed, and treating it
 * as unknown would hold every caller's guard on any local bug.
 *
 * This is deliberately separate from `isTransientInfraError`: that predicate
 * answers "would retrying a read help", this one answers "might the write
 * have happened".
 */
export function classifyMutationFailure(
  error: unknown
): MutationFailureOutcome {
  if (!isRecord(error) || !("status" in error)) return "definite";

  const { status } = error;

  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return "unknown";
  }

  if (status === "PARSING_ERROR") {
    const originalStatus = error.originalStatus;
    if (typeof originalStatus !== "number") return "unknown";
    return isClientErrorStatus(originalStatus) ? "definite" : "unknown";
  }

  if (typeof status === "number") {
    return isClientErrorStatus(status) ? "definite" : "unknown";
  }

  return "definite";
}
